// ======================================================================
// ImsPage.formatters.js
// Formatter components per colonne IMS Grid
//
// Ogni formatter riceve { value, row, column } da DataTypeProvider.
// Props aggiuntive (isMobile, expandedRow, ...) vengono passate
// tramite wrapper nel componente padre.
// ======================================================================

import React from "react";
import { IconButton } from "@material-ui/core";
import { TrendIcon } from "./ImsTrendIcons";
import { sortPeriodsChronologically } from "./ImsPeriodUtils";

// ======================================================================
// PRODOTTO: nome + pulsante dettagli
// ======================================================================
export const ProductFormatter = ({ value, row, isMobile, expandedRow, getRowKey, setExpandedRow }) => {
  if (value == null) return <span className="text-muted">&mdash;</span>;
  return (
    <div className="text-left text-small">
      <span style={{ fontSize: isMobile ? ".8rem" : ".9rem" }}>{value}</span>
      <br />
      <IconButton
        size="small"
        onClick={() => {
          const key = getRowKey(row);
          setExpandedRow(expandedRow === key ? null : key);
        }}
        className="px-0 text-muted"
      >
        {expandedRow === getRowKey(row) ? (
          <span style={{ fontSize: isMobile ? ".8rem" : ".9rem" }}>- Dettagli</span>
        ) : (
          <span style={{ fontSize: isMobile ? ".8rem" : ".9rem" }}>+ Dettagli</span>
        )}
      </IconButton>
    </div>
  );
};

// ======================================================================
// TREND: icona ▲ ▼ —
// ======================================================================
export const TrendFormatter = ({ value }) => {
  const trend = value === "positive" ? "positive" : value === "negative" ? "negative" : "neutral";
  return <TrendIcon trend={trend} />;
};

// ======================================================================
// DIFF: differenza media colorata
// ======================================================================
export const DiffFormatter = ({ value }) => {
  if (value == null) return <span className="text-muted text-small">&mdash;</span>;
  const sign = value > 0 ? "+" : "";
  const variant = value > 0 ? "success" : value < 0 ? "danger" : "secondary";
  return (
    <small className={`text-${variant} font-weight-bold`}>
      {sign}
      {value}
    </small>
  );
};

// ======================================================================
// PERCENTUALE: valore % colorato
// ======================================================================
export const PercentFormatter = ({ value }) => {
  if (value == null || isNaN(value)) return <span className="text-muted text-small">&mdash;</span>;
  const sign = value > 0 ? "+" : "";
  const variant = value > 0 ? "success" : value < 0 ? "danger" : "secondary";
  return (
    <small className={`text-${variant} font-weight-bold`}>
      {sign}
      {value}%
    </small>
  );
};

// ======================================================================
// NUMERO: formattazione locale IT
// ======================================================================
export const NumberFormatter = ({ value }) => {
  if (value == null) return <span className="text-muted text-small">&mdash;</span>;
  return <span>{Number(value).toLocaleString("it-IT")}</span>;
};

// ======================================================================
// VALUTA: formato €
// ======================================================================
export const CurrencyFormatter = ({ value }) => {
  if (value == null) return <span className="text-muted text-small">&mdash;</span>;
  return (
    <span>
      &euro;{Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </span>
  );
};

// ======================================================================
// VALUTA COLORATA: confronto col periodo precedente
// ======================================================================
export const CurrencyFormatterColored = ({ value, row, column }) => {
  if (value == null || isNaN(value)) return <span className="text-muted text-small">&mdash;</span>;

  const colName = column?.name;
  const period =
    typeof colName === "string" && colName.startsWith("fatturato_") ? colName.replace("fatturato_", "") : null;

  if (!period || !row?.fatturato) return <CurrencyFormatter value={value} />;

  const allPeriods = sortPeriodsChronologically(Object.keys(row.fatturato || {}));
  const isAggregated = period.includes(" - ");
  const relevantPeriods = allPeriods.filter((p) => (isAggregated ? p.includes(" - ") : !p.includes(" - ")));
  const index = relevantPeriods.indexOf(period);
  const prevPeriod = index > 0 ? relevantPeriods[index - 1] : null;
  const prevValue = prevPeriod != null ? row.fatturato?.[prevPeriod] : null;
  const variant =
    prevValue != null && !isNaN(prevValue)
      ? Number(prevValue) < Number(value)
        ? "success"
        : "danger"
      : "secondary";

  return (
    <small className={`text-${variant} font-weight-bold`}>
      &euro;{Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </small>
  );
};

// ======================================================================
// VALUTA COLORATA PER TREND: usa il trend della riga
// ======================================================================
export const CurrencyFormatterTrendColored = ({ value, row }) => {
  if (value == null || isNaN(value)) return <span className="text-muted text-small">&mdash;</span>;
  const variant = row?.trend === "negative" ? "danger" : "success";
  return (
    <small className={`text-${variant} font-weight-bold`}>
      &euro;{Number(value).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </small>
  );
};
